import findLastIndex from 'lodash/findLastIndex'
import React, { useMemo, useState, useEffect } from 'react'
import { Box, BoxProps, Flex, Text } from '@fundamentals'
import { QuestionWrapper } from './QuestionWrapper'
import { Card } from '@common/material'
import { useTheme, Collapse } from '@mui/material'
import { KeyboardArrowDown } from '@mui/icons-material'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { QuestionGroupProps } from '@core/components/templateAnswersForm'
import useConstants from '@core/providers/useConstants'
import { ConditionalQuestionGroup } from './ConditionalQuestionGroup'
import { Palette } from '@core/theme/color-palette-siteassist'

const QuestionGroup: React.FC<BoxProps & QuestionGroupProps> = ({
  questionGroup: { conditionalQuestionGroup, ...questionGroup },
  questionsFormKey = 'questions',
  groupIndex,
  readOnly,
  warnings,
  onlyShowWarnings,
  projectId,
  submittedBy,
  sx = {
    mb: 2,
  },
}) => {
  const theme: any = useTheme()
  const isSingular = questionGroup.singular
  const formContext = useFormContext()
  const { constants } = useConstants()

  const template = formContext?.getValues('template')
  const isInspectionChecklist = template?.usedForInspections
  const hasErrors = !!formContext?.formState?.errors?.questions?.[groupIndex]
  const isSubmitting = formContext?.formState?.isSubmitting

  const [collapsed, setCollapsed] = useState(() => {
    const groups = formContext?.getValues(questionsFormKey)
    const questionGroupCount = Array.isArray(groups) ? groups.length : 0
    return (
      !!isInspectionChecklist &&
      !hasErrors &&
      !isSingular &&
      questionGroupCount >
        constants.inspection_question_groups_collapse_threshold
    )
  })

  const hiddenQuestionIds: Array<string> = !formContext
    ? null
    : useWatch({
        name: 'hiddenQuestionIds',
      })

  const questionIndicesById = useMemo(() => {
    return questionGroup.data.reduce((acc, question, index) => {
      acc[question.id] = index
      return acc
    }, {})
  }, [])

  const visibleQuestionIdsSet = useMemo(() => {
    const hiddenQuestionIdsSet = new Set(
      hiddenQuestionIds ||
        questionGroup.data
          .filter((question) => question.isHidden)
          .map((question) => question.id),
    )
    const visibleQuestionIds = questionGroup.data.reduce((acc, question) => {
      if (!hiddenQuestionIdsSet.has(question.id)) acc.add(question.id)

      return acc
    }, new Set<string>())

    return visibleQuestionIds
  }, [hiddenQuestionIds])

  const lastQuestionIndex = useMemo(() => {
    return findLastIndex(questionGroup.data, (question) => {
      return visibleQuestionIdsSet.has(question.id)
    })
  }, [visibleQuestionIdsSet])

  const questionGroupWarnings = useMemo(() => {
    return warnings?.filter((warning) =>
      visibleQuestionIdsSet?.has(warning?.questionId),
    )
  }, [warnings, visibleQuestionIdsSet])

  useEffect(() => {
    // Expand inspection checklist groups with errors or when submitting automatically
    if ((hasErrors && isInspectionChecklist) || isSubmitting) {
      setCollapsed(false)
    }
  }, [hasErrors, isInspectionChecklist, isSubmitting])

  const isQuestionGroupHidden =
    (hiddenQuestionIds === null && questionGroup?.isHidden) ||
    hiddenQuestionIds?.includes(questionGroup.id) ||
    (visibleQuestionIdsSet?.size === 0 && !conditionalQuestionGroup)

  if (isQuestionGroupHidden) return null

  if (onlyShowWarnings && !questionGroupWarnings?.length) {
    // Hide groups that have no warnings if the onlyShowWarnings flag is passed
    return null
  }

  return (
    <Card
      sx={{
        ...sx,
        bgcolor: isSingular ? 'auto' : Palette.Grey[200],
        borderColor: isSingular ? 'auto' : Palette.Grey[200],
        p: isSingular ? 2 : 1,
        pb: isSingular ? undefined : collapsed ? 0 : 1,
      }}
      data-test={`question-group-${groupIndex}`}
      data-id={questionGroup.id}
    >
      {!isSingular && (
        <Flex
          alignItems='center'
          justifyContent='space-between'
          mb={1}
          onClick={() => {
            if (isInspectionChecklist) {
              setCollapsed(!collapsed)
            }
          }}
          sx={{
            cursor: isInspectionChecklist ? 'pointer' : 'default',
          }}
        >
          {questionGroup.title && (
            <Text
              sx={{
                fontSize: '14px',
              }}
            >
              {questionGroup.title}
            </Text>
          )}
          {isInspectionChecklist && (
            <KeyboardArrowDown
              sx={{
                transform: `rotate(${collapsed ? 0 : 180}deg)`,
                transition: '.3s ease',
                color: Palette.Grey[600],
              }}
            />
          )}
        </Flex>
      )}

      <Collapse in={!collapsed}>
        <Box sx={{ mt: !isSingular ? 1 : 0 }}>
          {conditionalQuestionGroup &&
            (!formContext ? (
              <ConditionalQuestionGroup
                conditionalQuestionGroup={conditionalQuestionGroup}
                readOnly={readOnly}
              />
            ) : (
              <Controller
                name={`${questionsFormKey}.${groupIndex}.conditionalQuestionGroup.submissionText`}
                rules={{ required: true }}
                render={({ field, fieldState: { error } }) => {
                  return (
                    <Box
                      sx={{
                        mt: error ? 2 : 0,
                        outlineWidth: error ? '2px' : '0',
                        // @ts-ignore
                        outlineColor: theme.palette.error.main,
                        outlineStyle: 'solid',
                        outlineOffset: '5px',
                        borderRadius: 0.1,
                        transition: 'all 0.3s',
                        padding: error ? 1.5 : 0,
                        pb: error ? 2 : 0,
                      }}
                    >
                      <ConditionalQuestionGroup
                        conditionalQuestionGroup={conditionalQuestionGroup}
                        inputProps={field}
                        readOnly={readOnly}
                      />
                    </Box>
                  )
                }}
              />
            ))}

          {questionGroup.data.map((question, questionIndex) => {
            if (!visibleQuestionIdsSet?.has(question.id)) return null
            if (
              onlyShowWarnings &&
              !questionGroupWarnings?.find(
                (warning) => warning.questionId === question.id,
              )
            ) {
              return null
            }

            const isLastQuestion = questionIndex === lastQuestionIndex

            return (
              <Box
                key={question.id}
                mb={!isLastQuestion && 1}
                sx={{
                  borderWidth: 1,
                  p: isSingular ? 0 : 2,
                  bgcolor: isSingular ? 'auto' : 'white',
                  borderRadius: isSingular ? 0 : 1,
                  border: isSingular
                    ? 'none'
                    : `1px solid ${Palette.Grey[300]}`,
                }}
              >
                <QuestionWrapper
                  groupIndex={groupIndex}
                  questionIndex={questionIndex}
                  questionNumber={questionIndicesById[question.id] + 1}
                  answer={!formContext && question}
                  projectId={projectId}
                  readOnly={readOnly}
                  questionsFormKey={questionsFormKey}
                  warnings={warnings?.filter(
                    (warning) => warning?.questionId === question?.id,
                  )}
                  submittedBy={submittedBy}
                />
              </Box>
            )
          })}
        </Box>
      </Collapse>
    </Card>
  )
}

export { QuestionGroup }
